import React, { useState, useEffect, useRef, useContext } from 'react';
import { useSearchBox, Index, Configure } from 'react-instantsearch';
import { baseConfig } from '../../config/algolia';
import GtmContext from '../../contexts/GtmContext';
import { pushToDataLayer } from '@jbhi-fi/utils/src/ga4Tracking/datalayer';
import PriceRangeDropdown from './PriceRangeDropdown';
import QuickResults from './QuickResults';
import QuickSuggestions from './QuickSuggestions';
import { getStoreCurrency } from '../../utils/shopify/getStoreCurrency';
import { buildSearchFilters } from '@jbhi-fi/utils/src/buildAlgoliaSearchFilters/buildAlgoliaSearchFilters';
import { QuickSearchContent } from '@jbhi-fi/components/src/QuickSearchContent/QuickSearchContent';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import { getSearchContentSuggestions } from './getSearchContentSuggestions';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import { useFeatureFlag } from '../../hooks/useFeatureFlag/useFeatureFlag';

let timerId = undefined;
const DEBOUNCE_TIME = 400;

const queryHook = (query, search) => {
  if (timerId) {
    clearTimeout(timerId);
  }

  timerId = setTimeout(() => search(query), DEBOUNCE_TIME);
};

const SearchBar = ({
  closeSearch,
  onSearch,
  showQuickResults,
  searchTerm,
  setSearchTerm,
  setShowQuickResults,
  selectionRange,
  setRangeValues,
}) => {
  const { refine } = useSearchBox({ queryHook });
  const searchInput = useRef(null);
  const [inputError, setInputError] = useState('');
  const [showPriceRange, setShowPriceRange] = useState(false);
  const [priceRange, setPriceRange] = useState({});
  const [saleItems, setSaleItems] = useState(false);
  const [nbSaleItems, setNbSaleItems] = useState(0);
  const [nbHits, setNbHits] = useState(0);
  const { gtm, setGtm } = useContext(GtmContext);
  const debouncedStateRef = useRef(null);
  const debouncedGtmStateRef = useRef(null);
  const lastGtm = useRef(null);
  const [isEnterKey, setIsEnterKey] = useState(false);
  const [contentSuggestions, setContentSuggestions] = useState([]);
  const isDesktop = useBreakpoint('md', 'up');
  const isSearchOverlayNewDesign = useFeatureFlag('isSearchOverlayNewDesign');
  const contentSuggestionsVariant = useFeatureFlag('searchContentSuggestionsVariant');

  const setSuggestedContent = async searchTerm => {
    if (contentSuggestionsVariant) {
      const suggestions = await getSearchContentSuggestions(searchTerm);
      setContentSuggestions(suggestions);
      return suggestions;
    }
  };

  useEffect(() => {
    if (JSON.stringify(lastGtm.current) !== JSON.stringify(gtm) && gtm.search && gtm.suggestions) {
      clearTimeout(debouncedGtmStateRef.current);

      debouncedGtmStateRef.current = setTimeout(() => {
        // Push gtm view event
        window.dataLayer.push({
          ...gtm,
          event: 'custom.search.suggested.results',
          ecommerce: {
            ...gtm.ecommerce,
            currencyCode: getStoreCurrency(),
            impressions: gtm.ecommerce.impressions ? [...gtm.ecommerce.impressions] : 0,
          },
        });

        // Set last gtm state
        lastGtm.current = gtm;
      }, DEBOUNCE_TIME);
    }
  }, [gtm]);

  /**
   * Focus on input
   */
  useEffect(() => {
    pushToDataLayer({
      event: 'start_search',
      search_type: 'suggested search',
    });
    const inputValue = document.querySelector('#quicksearch-search-box input').value;
    clearTimeout(debouncedStateRef.current);

    if (inputValue) {
      refine(inputValue);
      setSuggestedContent(inputValue);
      setSearchTerm(inputValue);
      setShowPriceRange(true);
      setShowQuickResults(true);
    }
    // Hide minicart if not in Cart Page
    const isCartPage = Boolean(document.querySelector('[data-cart-page]'));
    if (!isCartPage) {
      document.querySelector('body').classList.remove('minicart-open');
      document.querySelector('.mini-cart-container').classList.remove('is-visible');
    }

    return () => {
      clearTimeout(debouncedGtmStateRef.current);
      clearTimeout(debouncedStateRef.current);
    };
  }, []);

  useEffect(() => {
    if (selectionRange && selectionRange.end !== -1 && searchInput !== null) {
      searchInput.current.focus();
      searchInput.current.setSelectionRange(selectionRange.start, selectionRange.end);
    }
  }, [searchTerm]);

  /**
   * Function handler upon input change/keypress
   */
  const handleChange = event => {
    const { value = '' } = event.target;
    clearTimeout(debouncedStateRef.current);

    // Ensure error is cleared
    setInputError('');

    // Set search term
    setSearchTerm(value);
    setPriceRange({});
    setSaleItems(false);

    if (value.length < 2) {
      setShowPriceRange(false);
      setShowQuickResults(false);
      return;
    }

    setShowPriceRange(!!value);
    setShowQuickResults(!!value);

    debouncedStateRef.current = setTimeout(async () => {
      const suggestions = await setSuggestedContent(value);
      setGtm({
        ...gtm,
        suggestions: {
          ...gtm.suggestions,
          content: suggestions?.map((suggestion, i) => ({
            label: suggestion.title,
            position: i + 1,
          })),
        },
        search: {
          ...gtm.search,
          term: value,
        },
      });
    }, DEBOUNCE_TIME);
  };

  /**
   * Upon search submit/enter, validate there's a search term in the field before triggering onSearch event
   */
  const validateInput = (event, method) => {
    event.preventDefault();
    if (!method) {
      method = isEnterKey ? 'press enter' : 'click submit button';
    }

    // If < 2 written in search input, show an error
    if (searchTerm.length < 2) {
      setInputError("Please enter a product or brand… (e.g. 'Laptop' or 'Apple')");
      if (searchInput) {
        searchInput.current.focus();
      }
    } else {
      // Let's search!
      onSearch(searchTerm, method, priceRange, saleItems);
    }
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      setIsEnterKey(true);
    }
  };

  const trackCloseSearch = () => {
    pushToDataLayer({
      event: 'close_search',
      method: 'close icon',
      searchTerm: searchTerm,
    });
  };

  /**
   * Render function
   */
  return (
    <>
      <div className={`search-bar ${isSearchOverlayNewDesign ? '' : 'old'}`}>
        <button
          className={`close-search ${isSearchOverlayNewDesign ? '' : 'old'}`}
          aria-label="Close Search"
          title="Close Search"
          onClick={() => {
            closeSearch();
            trackCloseSearch();
          }}
        />
        <form onSubmit={e => validateInput(e)}>
          <input
            type="text"
            className={`input-search${inputError && ' error'}`}
            value={searchTerm}
            onChange={handleChange.bind(this)}
            placeholder={inputError ? inputError : 'Search products, brands, and more…'}
            ref={searchInput}
            autoComplete="true"
            autoCapitalize="none"
            autoFocus
            onKeyDown={handleKeyDown}
          />
          {showPriceRange ? (
            <Index indexId="quickresults" indexName={baseConfig.product_families_index}>
              <Configure clickAnalytics hitsPerPage={4} filters={buildSearchFilters()} distinct query={searchTerm} />

              <PriceRangeDropdown
                setPriceRange={setPriceRange}
                setSaleItems={setSaleItems}
                nbHits={nbHits}
                nbSaleItems={nbSaleItems}
                setRangeValues={setRangeValues}
                searchTerm={searchTerm}
              />
            </Index>
          ) : null}
          <button type="submit" className="submit-search" aria-label="Start Search" title="Start Search">
            Search
          </button>
        </form>
      </div>
      {showQuickResults ? (
        <div className="search-scroll-container">
          <QuickResults
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            validateInput={e => validateInput(e, 'click view all results')}
            onSearch={onSearch}
            priceRange={priceRange}
            setNbHits={setNbHits}
            setNbSaleItems={setNbSaleItems}
            saleItems={saleItems}
            nbSaleItems={nbSaleItems}
            contentSuggestions={contentSuggestionsVariant === 'list' ? contentSuggestions : []}
          />
          {contentSuggestionsVariant === 'cards' && contentSuggestions?.length ? (
            <div className={`suggested-content-container ${isSearchOverlayNewDesign ? '' : 'old'}`}>
              <CalloutFont>News & Reviews</CalloutFont>
              <div className="suggested-content">
                {isDesktop ? (
                  contentSuggestions?.map((suggestion, i) => <QuickSearchContent key={i} {...suggestion} />)
                ) : (
                  <Carousel showPagination={false} gradientColor={palette.secondary.main} fullBleed>
                    {contentSuggestions?.map((suggestion, i) => (
                      <QuickSearchContent key={i} {...suggestion} />
                    ))}
                  </Carousel>
                )}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      {searchTerm.length === 0 && <QuickSuggestions onSearch={onSearch.bind(this)} searchTerm={searchTerm} />}
    </>
  );
};
//);

export default SearchBar;
